import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  addMonths,
  endOfMonth,
  eachMonthOfInterval,
  startOfMonth,
} from 'date-fns'

import AdminScheduleContainer from '../../../AdminScheduleContainer/AdminScheduleContainer'
import ScheduleFeedback from '../../../ScheduleFeedback/ScheduleFeedback'
import { formatCpf, getRoleFromProfile } from 'utils/textTransformations'

import { PortalModal } from 'components/PortalModal/PortalModal'
import Dropdown from 'components/Dropdown'

import { listProfessionalService } from 'services/listProfessionalService'
import { getCorporateEligibility } from 'services/corporateEligibilityService'
import { getProfessionalAvailability } from 'services/professionalAvailabilityService'
import ReservationService from 'services/reservation'
import createOrder from 'services/order/createOrder'
import { getLastValidityDetail } from 'services/lastValidityDetail'
import createCheckout from 'services/order/createCheckout'

import {  saveOrder } from 'redux/actions/ordersActions'

import { PSYCHOLOGIST } from 'settings/_profileSettings'
import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'
import { Spinner, Typography } from '@telavita-core/react-design-kit'
import { ACTIVE, INACTIVE } from 'settings/_personSettings'

import { getProducts } from 'redux/actions'
import { ReservationRecurrence } from 'models/Reservation'

const ModalAdminSchedule = ({ patient, onClose }) => {
  const dispatch = useDispatch()

  const authUserPlanGroupCode = useSelector(state => state?.auth?.planSelected?.plan_group?.code)


  const [step, setStep] = useState('SCHEDULE')
  const [reservations, setReservations] = useState([])
  const [availabilities, setAvailabilities] = useState([])
  const [schedules, setSchedules] = useState([])
  const [createdSchedules, setCreatedSchedules] = useState([])
  const [loadingProfessionals, setLoadingProfessionals] = useState(false)
  const [loadingSubmitSchedule, setLoadingSubmitSchedule] = useState(false)
  const [loadingAvailabilities, setLoadingAvailabilities] = useState(false)
  const [professionals, setProfessionals] = useState(null)
  const [createdOrder, setCreatedOrder] = useState(null)
  const [hasCheckout, setHasCheckout] = useState(null)
  const [selectedProfessional, setSelectedProfessional] = useState({})
  const [error, setError] = useState(null)

  const [selectedPlan, setSelectedPlan] = useState()

  const [maxSchedules, setMaxSchedules] = useState(null)
  const [
    showTreatmentNotStartedMessage,
    setShowTreatmentNotStartedMessage,
  ] = useState()
  const [eligibilityLoading, setEligibilityLoading] = useState(false)

  const patientPlans = patient?.person?.plans 
    ? patient?.person?.plans.reduce((acc, curr) => {
      if (curr.plan.group.code === authUserPlanGroupCode && curr.plan.status_code !== INACTIVE) {
        acc.push({
          id: curr.plan.code,
          name: curr.plan.name,
        })
      }
      return acc
    }, []).sort((a, b) => (a.name > b.name) ? 1 : -1)
    : []

  const planGroupFrquency = selectedPlan
    ? selectedPlan.plan.group.frequency
    : null

  const [
    fetchedAvailabilitiesMonths,
    setFetchedAvailabilitiesMonths,
  ] = useState([])

  const fromMonth = new Date()
  const toMonth = endOfMonth(addMonths(new Date(), 3))
  const products = useSelector((state) => state.products.products)

  const selectedProduct = products[0]

  const {
    ENTERPRISE,
    CARE_PLUS,
    CORPORATE_LIMITED,
    CORPORATE_UNLIMITED,
  } = PlanGroupCategoryEnum

  useEffect(() => {
    dispatch(getProducts(PSYCHOLOGIST))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getProfessionals = (specialty, query) => {
    setLoadingProfessionals(true)

    setAvailabilities([])
    setFetchedAvailabilitiesMonths([])

    listProfessionalService({
      profileRole: specialty.code,
      statusCode: ACTIVE,
      planCode: selectedPlan.plan_code,
      query: query,
    }).then((res) => {
      setLoadingProfessionals(false)
      setProfessionals(res.persons)
    })
  }

  const onSubmitSchedule = async (availabilities, professional) => {
    setLoadingSubmitSchedule(true)
    setSelectedProfessional(professional)

    const payload = {
      product_group_code:
        professional.person_profile[0].products[0].product.product_group.code,
      plan_code: selectedPlan.plan.code,
      profile_role: patient.profileRole,
      username: patient.person.username,
      person_profile_product_appointment_id:
        professional.person_profile[0].products[0].appointments[0].id,
      schedules: availabilities.map((availability) => {
        return {
          start_date: availability.startDate,
          end_date: availability.endDate,
          guests: [
            {
              person_profile_id: professional.person_profile[0].id,
            },
            {
              person_profile_id: patient.person.person_profile_id,
            },
          ],
        }
      }),
    }

    await dispatch(saveOrder(payload))
    const { orderId, instantCheckout } = await createOrder(payload)
    const checkoutResponse = await createCheckout({
      orderId,
    })

    if (orderId && checkoutResponse === 'OK') {
      setCreatedOrder(orderId)
      setCreatedSchedules(
        availabilities.map((availability) => availability.startDate)
      )
      setHasCheckout(instantCheckout)
    }

    setLoadingSubmitSchedule(false)
    setStep('SUCCESS')
  }

  const onSelectProfessional = async (professional) => {
    if (!professional) return

    setLoadingAvailabilities(true)
    setAvailabilities([])
    setSchedules([])
    setError(null)
    setReservations([])

    ReservationService.getReservations({
      username: patient.person.username,
      isProfessional: true,
    })
      .then((reservations) => {
        setReservations(reservations)
      })
      .catch((error) => {
        console.error(error)
        setReservations([])
      })

    await getAvailabilities(professional)
  }

  const getAvailabilities = async (professional) => {
    const interval = eachMonthOfInterval({ start: fromMonth, end: toMonth })

    for await (let month of interval) {
      await getProfessionalAvailability({
        slug: professional.person_general.email,
        startDate: startOfMonth(month).toISOString(),
        endDate: endOfMonth(month).toISOString(),
        planCode: selectedPlan.plan.code,
        productCode: selectedProduct.code,
        username: patient.person.username,
        groupBy: 'date',
        profileRole: getRoleFromProfile(
          professional.person_profile[0].profile_code
        ),
      })
        .then(({ availabilities, schedules }) => {
          setFetchedAvailabilitiesMonths((prev) => [...prev, month.getMonth()])
          setLoadingAvailabilities(false)
          setAvailabilities((prev) => [...prev, ...availabilities])
          setSchedules((prev) => [...prev, ...schedules])
        })
        .catch((error) => {
          setLoadingAvailabilities(false)
          setAvailabilities([])
          setSchedules([])
          setError(error)
        })
    }
  }

  const handleSelectPlan = (id) => {
    setEligibilityLoading(true)
    setAvailabilities([])
    setSchedules([])
    setError(null)
    setReservations([])
    setShowTreatmentNotStartedMessage(false)
    setMaxSchedules(null) 

    const currentPlan = patient.person.plans.find(
      (plan) => plan.plan.code === id
    )

    const planGroupCategory = currentPlan.plan.group.category
    const isCorporate = [CORPORATE_LIMITED, CORPORATE_UNLIMITED].includes(
      planGroupCategory
    )
    const isCorporateUnlimited = planGroupCategory === CORPORATE_UNLIMITED
    const isEnterprise = [CARE_PLUS, ENTERPRISE].includes(planGroupCategory)

    if (isCorporate) {
      getCorporateEligibility({
        username: patient.person.username,
        planCode: currentPlan.plan.code
      }).then((res) => {
        setEligibilityLoading(false)

        if (isCorporateUnlimited) return

        setMaxSchedules(res.availableCredits) 
      }).catch(() => setEligibilityLoading(false))
    } else if (isEnterprise) {
      getLastValidityDetail(patient.person.email, currentPlan.plan.code)
        .then((validityDetail) => {
          setMaxSchedules(validityDetail.availableCredits)
          setEligibilityLoading(false)
          setShowTreatmentNotStartedMessage(false)
        })
        .catch((error) => {
          if (error === 404) setShowTreatmentNotStartedMessage(true)
          setEligibilityLoading(false)
          console.error(error)
        })
    }
    setSelectedPlan(currentPlan)
    setEligibilityLoading(false)
  }

  const onMonthChange = (date) => {
    if (!fetchedAvailabilitiesMonths.includes(date.getMonth()))
      setLoadingAvailabilities(true)
  }

  const recurrentReservation = reservations?.find(reservation => reservation.recurrence === ReservationRecurrence.WEEKLY)
  
  return (
    <PortalModal
      title={step === 'SUCCESS' ? 'Agendamento concluído' : 'Agendar consulta'}
      subtitle={
        <>
          <div>
            Para <b>{patient?.person?.full_name || ''}</b>
          </div>
          <div>CPF {formatCpf(patient?.person?.cpf || '')}</div>
        </>
      }
      onClose={onClose}
    >
      <div className='ModalAdminSchedule'>
        <div className='ModalAdminSchedule__Content'>
          {!createdOrder && (
            <>
              <div className='ModalAdminSchedule__Content__DropdownWrapper'>
                <Dropdown
                  dropDownOverflow
                  dropDownItensToShow='3'
                  name='plan-list'
                  options={patientPlans}
                  clearSelection={selectedPlan === null}
                  onSelect={(id) => handleSelectPlan(id)}
                  placeholder='Selecione o plano'
                />
                {eligibilityLoading && <div className='ModalAdminSchedule__Content__LoadingContainer'><Spinner /></div>}
                {showTreatmentNotStartedMessage && !eligibilityLoading && (
                  <div className='ModalAdminSchedule__Content__TreatmentNotStartedMessage'>
                    <Typography variant='content2'>
                      O paciente ainda não iniciou um tratamento com um{' '}
                      <b>psicólogo</b>.
                    </Typography>
                    <Typography variant='content2'>
                      Para iniciar o tratamento, ele deve clicar no botão{' '}
                      <i>“Solicitar consulta com psiquiatra”</i> em seu painel
                      de convênio.
                    </Typography>
                  </div>
                )}
              </div>
              {selectedPlan &&
                !eligibilityLoading &&
                !showTreatmentNotStartedMessage && (
                <AdminScheduleContainer
                  frequency={planGroupFrquency}
                  professionals={professionals}
                  availabilities={availabilities}
                  schedules={schedules}
                  reservationDate={
                    recurrentReservation && !error && recurrentReservation.startDate
                  }
                  reservationExceptions={
                    recurrentReservation && recurrentReservation?.recurrencyExceptions
                  }
                  error={error}
                  loading={loadingAvailabilities}
                  loadingSubmit={loadingSubmitSchedule}
                  loadingProfessionals={loadingProfessionals}
                  onSearch={getProfessionals}
                  onSubmit={onSubmitSchedule}
                  onSelectProfessional={onSelectProfessional}
                  fromMonth={fromMonth}
                  toMonth={toMonth}
                  maxSchedules={maxSchedules}
                  enableNewPatients={
                    selectedProfessional
                      ? selectedProfessional.enable_new_patients
                      : false
                  }
                  onMonthChange={onMonthChange}
                />
              )}
            </>
          )}
          {createdOrder && (
            <ScheduleFeedback
              orderId={createdOrder}
              hasCheckout={hasCheckout}
              schedules={createdSchedules}
              professional={selectedProfessional}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </PortalModal>
  )
}

export default ModalAdminSchedule
